import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';
import { useOwnerApi, commercialError } from '../commercial/useCommercialApi';

/**
 * GOV13-A5.3 — SuperAdmin Rate Card Management (governed authoring workflow).
 *
 * Backend-authoritative: consumes /super-admin/commercial/rate-card/* and NEVER invents lifecycle, publishes by
 * local state, calculates effective-version authority, or activates without the backend readiness gate. Separate
 * concepts are shown separately (lifecycle vs emitter-readiness vs metering-enabled) — an activity that is
 * lifecycle=ACTIVE with metering OFF renders "READY — charging disabled", never "LIVE". Draft-only edits;
 * published versions render read-only. Every mutation disables while pending (no duplicate submission) and shows
 * the exact backend rejection (no false success). Session expiry defers to the existing graceful re-auth.
 */
const LC_TONE = {
  ACTIVE: 'bg-green-100 text-green-800', INCLUDED: 'bg-blue-100 text-blue-800',
  RESERVED: 'bg-amber-100 text-amber-800', DEFERRED: 'bg-purple-100 text-purple-800',
  RETIRED: 'bg-gray-200 text-gray-600',
};
const VS_TONE = {
  draft: 'bg-gray-100 text-gray-700', validated: 'bg-blue-100 text-blue-800', approved: 'bg-indigo-100 text-indigo-800',
  published: 'bg-green-100 text-green-800', effective: 'bg-green-200 text-green-900', superseded: 'bg-gray-200 text-gray-600',
  retired: 'bg-gray-200 text-gray-500',
};
const Pill = ({ v, tone }) => <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${tone || 'bg-gray-100 text-gray-600'}`}>{v}</span>;
const LIFECYCLES = ['ACTIVE', 'INCLUDED', 'RESERVED', 'DEFERRED', 'RETIRED'];
// which version action is offered for a given version state (governed sequence, one action per step)
const NEXT_ACTIONS = {
  draft: [['validate', 'Validate'], ['retired', 'Retire']],
  validated: [['approved', 'Approve'], ['draft', 'Return to draft'], ['retired', 'Retire']],
  approved: [['published', 'Publish'], ['draft', 'Revoke approval']],
  published: [['effective', 'Schedule effective'], ['superseded', 'Supersede'], ['retired', 'Retire']],
  effective: [['superseded', 'Supersede'], ['retired', 'Retire']],
  superseded: [['retired', 'Retire']],
  retired: [],
};

export default function RateCardManagement() {
  const { superAdmin } = useSuperAdminAuth();
  const api = useOwnerApi(superAdmin);
  const [lifecycle, setLifecycle] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selected, setSelected] = useState(null);     // version detail
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);            // duplicate-submission protection
  const [runtime, setRuntime] = useState(null);       // canonical environment runtime gate state (/metering-status)
  const [provider, setProvider] = useState(null);     // GOV-7 Path B: external-provider USD usage rates (distinct instrument)

  const loadTop = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [lc, vs, rt, pu] = await Promise.allSettled([
        api.get('/api/v1/super-admin/commercial/rate-card/activity-lifecycle'),
        api.get('/api/v1/super-admin/commercial/rate-card/authoring/versions'),
        api.get('/api/v1/super-admin/commercial/metering-status'),   // canonical runtime gate projection
        api.get('/api/v1/super-admin/commercial/provider-usage-rates'),  // USD provider usage (NOT credits)
      ]);
      if (lc.status === 'fulfilled') setLifecycle(lc.value.data); else throw lc.reason;
      setVersions(vs.status === 'fulfilled' ? (vs.value.data?.versions || []) : []);
      setRuntime(rt.status === 'fulfilled' ? rt.value.data : null);
      setProvider(pu.status === 'fulfilled' ? pu.value.data : null);
    } catch (e) { setError(commercialError(e)); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { loadTop(); }, [loadTop]);

  const openVersion = async (version) => {
    setBusy(true); setToast(null);
    try {
      const [d, a] = await Promise.allSettled([
        api.get(`/api/v1/super-admin/commercial/rate-card/authoring/versions/${version}`),
        api.get(`/api/v1/super-admin/commercial/rate-card/authoring/versions/${version}/audit`),
      ]);
      setSelected(d.status === 'fulfilled' ? d.value.data : null);
      setAudit(a.status === 'fulfilled' ? (a.value.data?.audit || []) : []);
    } catch (e) { setToast({ ok: false, msg: commercialError(e) }); }
    finally { setBusy(false); }
  };

  // ONE governed action dispatcher. Disables while pending; renders exact backend rejection; refreshes canonical
  // state. Never a local optimistic success.
  const run = async (fn, okMsg, reopen) => {
    if (busy) return;
    setBusy(true); setToast(null);
    try {
      await fn();
      setToast({ ok: true, msg: okMsg });
      await loadTop();
      if (reopen) await openVersion(reopen);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) { setToast({ ok: false, msg: 'Your SuperAdmin session expired. Please sign in again.' }); return; }
      const code = e?.response?.data?.detail?.code || e?.response?.data?.code;
      const det = e?.response?.data?.detail?.detail || e?.response?.data?.detail;
      const extra = det?.missing ? ` (missing: ${det.missing.join(', ')})` : det?.errors ? ` (${det.errors.map((x) => x.error || x).join(', ')})` : '';
      setToast({ ok: false, msg: code ? `Rejected: ${code}${extra}` : 'Action failed.' });
    } finally { setBusy(false); }
  };

  const bootstrap = () => run(() => api.post('/api/v1/super-admin/commercial/rate-card/bootstrap', {}), 'Bootstrapped first governed draft (v1).');
  const createDraft = (clone_from) => run(() => api.post('/api/v1/super-admin/commercial/rate-card/authoring/versions', clone_from ? { clone_from } : {}), 'Draft created.');
  const versionAction = (v, to) => {
    let effective_from;
    if (to === 'retired' && !window.confirm(`Retire rate-card version v${v.version}? This is governed and cannot be undone; the effective version is unaffected.`)) return;
    if (to === 'effective') { effective_from = window.prompt('Effective timestamp (ISO 8601, e.g. 2026-09-01T00:00:00+00:00):'); if (!effective_from) return; }
    return run(() => api.post(`/api/v1/super-admin/commercial/rate-card/authoring/versions/${v.version}/${to === 'validate' ? 'validate' : 'transition'}`, to === 'validate' ? {} : { to, effective_from }), `Version v${v.version} → ${to}.`, v.version);
  };
  const addActivity = (v) => {
    const key = (window.prompt('New activity_key (machine id):') || '').trim();
    if (!key) return;
    const label = window.prompt('Display label:') || key;
    const unit = window.prompt('Unit (e.g. response, minute):') || 'unit';
    // new activities ALWAYS default RESERVED server-side (never ACTIVE)
    return run(() => api.post(`/api/v1/super-admin/commercial/rate-card/authoring/versions/${v.version}/activities`, { activity_key: key, display_label: label, unit }), `Activity ${key} added (RESERVED).`, v.version);
  };
  const editActivity = (v, a) => {
    const credits = window.prompt(`Edit credits for ${a.activity_key} (draft only):`, String(a.credits ?? ''));
    if (credits == null) return;
    const patch = { credits: parseInt(credits, 10) };
    return run(() => api.patch(`/api/v1/super-admin/commercial/rate-card/authoring/versions/${v.version}/activities/${encodeURIComponent(a.activity_key)}`, patch), `Activity ${a.activity_key} updated.`, v.version);
  };
  const setLifecycle_ = (v, a, target) => run(() => api.post(`/api/v1/super-admin/commercial/rate-card/authoring/versions/${v.version}/activities/${encodeURIComponent(a.activity_key)}/lifecycle`, { target }), `Lifecycle → ${target}.`, v.version);

  if (loading) return <p className="text-sm text-gray-400">Loading rate-card lifecycle…</p>;
  if (error) return <div className="p-3 rounded bg-red-50 border border-red-200 text-sm text-red-700" role="alert">{error}</div>;

  const counts = lifecycle?.counts || {};
  const acts = lifecycle?.activities || [];
  const draftEditable = selected && (selected.state || 'draft') === 'draft';

  // Canonical environment runtime state — sourced from /metering-status + the governed effective version.
  // NEVER hardcoded. Separate meanings: LIFECYCLE vs EMITTER READINESS vs METERING STATUS vs ENFORCEMENT.
  const gates = runtime || {};
  const meteringOn = !!gates.metering_enabled;
  const cutoverOn = !!gates.rate_card_cutover_enabled;
  const enforcementOn = !!gates.enforcement_enabled;
  const ingestOn = gates.webhook_ingest_enabled;   // undefined when the projection does not expose it (not fabricated)
  const effectiveVersion = versions.find((v) => String(v.state || '') === 'effective');
  const GateChip = ({ label, on, unknown }) => (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${unknown ? 'bg-gray-100 text-gray-500' : on ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>
      {label}: {unknown ? '—' : on ? 'On' : 'Off'}
    </span>
  );
  // METERING STATUS is a distinct axis from LIFECYCLE. ACTIVE + metering ON = "Metering active" (governed debits
  // enabled); ACTIVE + metering OFF = "Ready — charging disabled". Non-ACTIVE keep their lifecycle meaning.
  const meteringStatusCell = (a) => {
    if (a.status === 'ACTIVE') {
      return meteringOn
        ? <Pill v="Metering active" tone="bg-green-100 text-green-800" />
        : <span className="text-amber-700">Ready — charging disabled</span>;
    }
    if (a.status === 'INCLUDED') return <span className="text-blue-700">Included in plan</span>;
    if (a.status === 'RESERVED') return <span className="text-amber-700">Not active — reserved</span>;
    if (a.status === 'DEFERRED') return <span className="text-purple-700">Deferred</span>;
    if (a.status === 'RETIRED') return <span className="text-gray-500">Retired</span>;
    return <span className="text-gray-400">Not metered</span>;
  };

  return (
    <div className="space-y-5">
      {toast && <div className={`p-2 rounded text-sm ${toast.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`} role="status">{toast.msg}</div>}

      {/* Environment-level runtime banner — owner-only; canonical /metering-status + governed effective version. */}
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="staging-runtime-banner">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-emerald-900">Staging Commercial Runtime</p>
            <p className="text-xs text-emerald-800">
              {meteringOn
                ? 'Governed metering and runtime debit recording are active in staging.'
                : 'Metering is off.'}{' '}
              Hard exhaustion enforcement {enforcementOn ? 'is active.' : 'remains off pending separate owner approval.'}
            </p>
          </div>
          <span className={`px-2 py-1 rounded text-xs font-bold ${enforcementOn ? 'bg-red-600 text-white' : meteringOn ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-700'}`}>
            {meteringOn ? (enforcementOn ? 'METERING + ENFORCEMENT ACTIVE' : 'METERING ACTIVE — ENFORCEMENT OFF') : 'METERING OFF'}
          </span>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-white border border-emerald-200 text-emerald-900">
            Rate-card version: {effectiveVersion ? `v${effectiveVersion.version} — Effective` : '—'}
          </span>
          <GateChip label="Cutover" on={cutoverOn} />
          <GateChip label="Metering" on={meteringOn} />
          <GateChip label="Enforcement" on={enforcementOn} />
          {ingestOn !== undefined && <GateChip label="Webhook ingest" on={!!ingestOn} />}
        </div>
      </div>

      {/* GOV-7 Path B — External-provider USD usage rates. A DISTINCT commercial instrument from the credit
          rate card below: real USD per unit for provider-backed operations, never Servisfy credits. */}
      {provider && Array.isArray(provider.rates) && provider.rates.length > 0 && (
        <Card className="bg-white shadow-md border-sky-200" data-testid="provider-usage-rates">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Provider Usage Rates <span className="text-sm font-normal text-sky-700">(USD per unit — external provider, not credits)</span></CardTitle>
            <p className="text-xs text-gray-500 mt-1">External-provider metered cost. Separate instrument from the credit Activity Lifecycle below — priced in USD, not Servisfy credits.</p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b"><tr>{['Meter', 'Provider', 'Unit', 'USD / unit', 'Billable event', 'Readiness', 'Lifecycle'].map(h => <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-100">
                {provider.rates.map((r) => (
                  <tr key={r.meter_key}>
                    <td className="px-3 py-2 font-mono text-xs text-gray-900 whitespace-nowrap">{r.meter_key}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{r.provider || '—'}</td>
                    <td className="px-3 py-2 text-xs">{r.unit}</td>
                    <td className="px-3 py-2 text-sm font-semibold text-sky-800 whitespace-nowrap">${Number(r.usd_per_unit).toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{r.billable_event}</td>
                    <td className="px-3 py-2"><Pill v={r.readiness_status} tone={r.readiness_status === 'READY' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'} /></td>
                    <td className="px-3 py-2"><Pill v={String(r.lifecycle || '').toUpperCase()} tone={r.lifecycle === 'EFFECTIVE' ? 'bg-green-200 text-green-900' : 'bg-gray-100 text-gray-600'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-gray-400 mt-2">{provider.note}</p>
          </CardContent>
        </Card>
      )}

      {/* Canonical activity lifecycle (all 16) */}
      <Card className="bg-white shadow-md">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Activity Lifecycle ({counts.TOTAL || acts.length})</CardTitle>
          <div className="text-xs text-gray-500">ACTIVE {counts.ACTIVE || 0} · INCLUDED {counts.INCLUDED || 0} · RESERVED {counts.RESERVED || 0} · DEFERRED {counts.DEFERRED || 0} · RETIRED {counts.RETIRED || 0}</div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>{['Activity', 'Lifecycle', 'Emitter readiness', 'Metering status', 'Reason / Included / Future'].map(h => <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-100">
              {acts.map((a) => (
                <tr key={a.activity_key}>
                  <td className="px-3 py-2 font-mono text-xs text-gray-900 whitespace-nowrap">{a.activity_key}</td>
                  <td className="px-3 py-2"><Pill v={a.status} tone={LC_TONE[a.status]} /></td>
                  <td className="px-3 py-2"><Pill v={a.readiness_status} tone={a.readiness_status === 'READY' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'} /></td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{meteringStatusCell(a)}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{a.availability_reason || (a.included_in ? `Included in ${a.included_in}` : '—')}{a.future_workstream ? ` · ${a.future_workstream}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Governed versions */}
      <Card className="bg-white shadow-md">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Governed Rate-Card Versions</CardTitle>
          <div className="flex gap-2">
            {versions.length === 0 && <Button size="sm" disabled={busy} onClick={bootstrap}>Bootstrap first draft</Button>}
            {versions.length > 0 && <Button size="sm" variant="outline" disabled={busy} onClick={() => createDraft()}>New draft (clone effective)</Button>}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {versions.length === 0 ? (
            <p className="text-sm text-gray-500">No governed version yet — the code seed is the current fallback authority. Bootstrap to create the first draft.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b"><tr>{['Version', 'State', 'Activities', 'Effective from', 'Immutable', 'Created by', ''].map(h => <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-100">
                {versions.map((v) => (
                  <tr key={v.version} className={selected?.version === v.version ? 'bg-purple-50' : ''}>
                    <td className="px-3 py-2">v{v.version}</td>
                    <td className="px-3 py-2"><Pill v={String(v.state || 'draft').toUpperCase()} tone={VS_TONE[v.state] || VS_TONE.draft} /></td>
                    <td className="px-3 py-2">{v.activity_count}</td>
                    <td className="px-3 py-2 text-xs">{v.effective_from || '—'}</td>
                    <td className="px-3 py-2">{v.immutable ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{v.created_by || '—'}</td>
                    <td className="px-3 py-2 flex gap-1">
                      <button disabled={busy} onClick={() => openVersion(v.version)} className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">Open</button>
                      <button disabled={busy} onClick={() => createDraft(v.version)} className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">Clone</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Version detail */}
      {selected && (
        <Card className="bg-white shadow-md">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Version v{selected.version} — <Pill v={String(selected.state || 'draft').toUpperCase()} tone={VS_TONE[selected.state] || VS_TONE.draft} /></CardTitle>
            <div className="flex flex-wrap gap-2">
              {draftEditable && <Button size="sm" variant="outline" disabled={busy} onClick={() => addActivity(selected)}>Add activity</Button>}
              {(NEXT_ACTIONS[selected.state || 'draft'] || []).map(([to, label]) => (
                <Button key={to} size="sm" variant={to === 'published' || to === 'effective' ? 'default' : 'outline'} disabled={busy} onClick={() => versionAction(selected, to)}>{label}</Button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {!draftEditable && <p className="mb-3 text-xs text-amber-700">This version is {selected.state} and immutable — to change an activity, create a new draft.</p>}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b"><tr>{['Activity', 'Lifecycle', 'Unit', 'Credits', 'Emitter', draftEditable ? 'Draft controls' : ''].map(h => <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(selected.activities || []).map((a) => (
                    <tr key={a.activity_key}>
                      <td className="px-3 py-2 font-mono text-xs">{a.activity_key}</td>
                      <td className="px-3 py-2"><Pill v={a.lifecycle_status} tone={LC_TONE[a.lifecycle_status]} /></td>
                      <td className="px-3 py-2">{a.unit || '—'}</td>
                      <td className="px-3 py-2">{a.lifecycle_status === 'INCLUDED' ? 'Included' : (a.credits ?? '—')}</td>
                      <td className="px-3 py-2"><Pill v={a.emitter_readiness || 'NOT_READY'} tone={a.emitter_readiness === 'READY' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'} /></td>
                      {draftEditable && (
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <button disabled={busy} onClick={() => editActivity(selected, a)} className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">Edit</button>
                            <select disabled={busy} value={a.lifecycle_status} onChange={(e) => setLifecycle_(selected, a, e.target.value)} className="text-xs border rounded px-1 py-0.5">
                              {LIFECYCLES.map((l) => <option key={l} value={l}>{l}</option>)}
                            </select>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-3">Changing an activity to ACTIVE runs the backend readiness gate — an owner selection alone cannot activate an unimplemented capability.</p>

            {/* Audit history */}
            <div className="mt-4 border-t pt-3">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Audit history</p>
              {audit.length === 0 ? <p className="text-xs text-gray-400">No audit entries.</p> : (
                <ul className="text-xs text-gray-700 space-y-0.5">
                  {audit.map((e, i) => <li key={i} className="font-mono">{e.timestamp || '—'} · {e.actor} · {e.action} {e.reason ? `· ${e.reason}` : ''}</li>)}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
